import type { ElementType, ReactNode } from "react";

// Eyebrow — canonical .eyebrow (Inter 600/11, UPPERCASE, +0.1em, --ink-faint).
// DS Eyebrow is distinct from the existing portal v2 atom (which uses
// --color-v2-* tokens); this one uses canonical tokens via .dova-eyebrow.

export interface EyebrowProps {
    children: ReactNode;
    as?: ElementType;
    className?: string;
    /** Back-compat (gen-1 atoms/Eyebrow): a Tailwind text-color class that
     *  overrides the default quiet `--eyebrow` grey, e.g.
     *  `text-[var(--warning)]`. When set, the eyebrow renders via utility
     *  classes (identical metrics to `.dova-eyebrow`) rather than the
     *  `.dova-eyebrow` rule — because that rule is unlayered and its `color`
     *  would otherwise win over an appended utility. Omit for the default. */
    color?: string;
}

export function Eyebrow({ children, as: Tag = "span", className = "", color }: EyebrowProps) {
    // Default path: canonical `.dova-eyebrow` (quiet --ink-faint eyebrow).
    if (!color) {
        return <Tag className={`dova-eyebrow ${className}`.trim()}>{children}</Tag>;
    }
    // Color-override path: reproduce .dova-eyebrow's metrics with utilities so
    // the caller's color class actually applies.
    const classes = ["[font:var(--type-eyebrow)] uppercase [letter-spacing:var(--tracking-eyebrow)]", color, className]
        .filter(Boolean)
        .join(" ");
    return <Tag className={classes}>{children}</Tag>;
}
